import { isSameDay } from 'date-fns'
import { computed, ref } from 'vue'
import { type LocationQueryRaw, useRoute } from 'vue-router'
import { formatDateForFilter } from '../lib'
import { type BaseSearchFilters, SearchSorting } from '../model'

type AvailableFilters = 'date' | 'sort' | 'tillPrice' | 'fromPrice' | 'categoryIds'

export const usePageFilters = (pageSize = 5, changeHandler?: () => void) => {
  const route = useRoute()

  /**
   * получает фильтры
   */
  const getFilters = (): BaseSearchFilters => {
    const base: any = {
      pagination: {
        pageSize: pageSize
      },
      filter: {}
    }
    const filtersFromRoute = getFiltersFromRoute()

    return {
      ...base,
      ...filtersFromRoute
    }
  }

  const getFiltersFromRoute = () => {
    //TODO: пока не прокидывется в query явно.
    // понять, нужно ли и реализовать
    if (!route.query) return {}

    const { fromDateTime, tillDateTime } = route.query
    if (!fromDateTime) return {}

    return {
      filter: {
        fromDateTime: fromDateTime as string,
        tillDateTime: tillDateTime as string
      }
    }
  }

  /**
   * явно апдейтит даты
   * @param value
   * @param skipChange
   */
  const updateDateFilters = (value?: Date | Date[], skipChange = false) => {
    if (!value) {
      delete pageFilters.value.filter.tillDateTime
      delete pageFilters.value.filter.fromDateTime

      return !skipChange && onChange()
    }

    pageFilters.value.filter!.fromDateTime = Array.isArray(value)
      ? formatDateForFilter(value[0])
      : formatDateForFilter(value)
    pageFilters.value.filter!.tillDateTime = Array.isArray(value)
      ? formatDateForFilter(value[1])
      : formatDateForFilter(value)

    !skipChange && onChange()
  }

  /**
   * апдейт сотировки
   * @param value
   * @param skipChange
   */
  const updateSorting = (value: SearchSorting, skipChange = false) =>
    (pageFilters.value.sorting = value) && !skipChange && onChange()
  //апдейт цен
  const updateFromPrice = (value?: string | number, skipChange = false) => {
    value || value === 0
      ? (pageFilters.value.filter!.fromPrice! = Number(value))
      : delete pageFilters.value.filter.fromPrice
    !skipChange && onChange()
  }
  const updateTillPrice = (value?: string | number, skipChange = false) => {
    value || value === 0
      ? (pageFilters.value.filter!.tillPrice! = Number(value))
      : delete pageFilters.value.filter.tillPrice
    !skipChange && onChange()
  }

  /**
   * апдейт категории
   */
  const updateCategoryIds = (value?: number[], skipChange = false) => {
    value
      ? (pageFilters.value.filter.categoryIds = value)
      : delete pageFilters.value.filter.categoryIds
    !skipChange && onChange()
  }

  /**
   * вычисленное значения для календаря
   */
  const datepickerValue = computed(() => {
    if (!pageFilters.value?.filter?.tillDateTime || !pageFilters.value?.filter?.fromDateTime)
      return undefined
    const { tillDateTime, fromDateTime } = pageFilters.value.filter

    if (isSameDay(tillDateTime!, fromDateTime)) return new Date(fromDateTime)

    return [new Date(fromDateTime), new Date(tillDateTime)]
  })

  /**
   * возвращает LocationQueryRaw со всеми предустановленными фильтрами
   */
  const getQueryFromFilters = (): LocationQueryRaw => {
    return {
      ...pageFilters.value.filter
    }
  }

  /**
   * вызывает хэндлер при изменении фильтров
   */
  const onChange = () => changeHandler && changeHandler()

  /**
   * сбрасывает фильтры
   * @param filtersToReset
   */
  const resetFilters = (filtersToReset?: AvailableFilters[]) => {
    const allFilters: AvailableFilters[] = ['date', 'tillPrice', 'fromPrice', 'categoryIds', 'sort']
    const updateFilters = (filters: AvailableFilters[]) => {
      filters.forEach((filter) => {
        switch (filter) {
          case 'date':
            updateDateFilters(undefined, true)
            break
          case 'tillPrice':
            updateTillPrice(undefined, true)
            break
          case 'fromPrice':
            updateFromPrice(undefined, true)
            break
          case 'categoryIds':
            updateCategoryIds(undefined, true)
            break
          case 'sort':
            updateSorting(SearchSorting.nearest, true)
            break
        }
      })
    }

    if (!filtersToReset) updateFilters(allFilters)
    else updateFilters(filtersToReset)

    onChange()
  }

  const pageFilters = ref<BaseSearchFilters>(getFilters())

  return {
    pageFilters,
    datepickerValue,
    getQueryFromFilters,

    updateDateFilters,
    updateSorting,
    updateFromPrice,
    updateTillPrice,
    updateCategoryIds,
    resetFilters
  }
}
